import { useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import { useFeatureFlag, Feature } from '@src/components'
import { Apollo, App } from '@src/config'
import { Login } from '@src/models/app.types'
import { userSelector } from '@src/selectors/app'
import { Storage, useDispatchAsync, useSnack } from '@src/utils'
import { useStorageValue } from '@src/utils/storage'

type IframeImpersonationFailureReason =
  | 'conflicting_request'
  | 'login_failed'
  | 'logout_failed'
  | 'missing_admin_context'
  | 'missing_endpoint'
  | 'user_mismatch'

type IframeImpersonationResultMessage =
  | {
      type: 'member_impersonated'
      userEmail: string
    }
  | {
      type: 'member_impersonation_failed'
      reason: IframeImpersonationFailureReason
      userEmail: string
    }

type BrowserLikeGlobal = typeof globalThis & {
  window?: BrowserLikeGlobal
  addEventListener?: (type: 'message', listener: (event: MessageEvent) => void) => void
  removeEventListener?: (type: 'message', listener: (event: MessageEvent) => void) => void
  parent?: {
    postMessage?: (
      message: IframeImpersonationResultMessage | { type: 'admin_authenticated' },
      targetOrigin: string,
    ) => void
  }
}

const browserGlobal = globalThis as BrowserLikeGlobal
const getBrowserWindow = (): BrowserLikeGlobal => browserGlobal.window ?? browserGlobal

const ALLOWED_FIREBASE_HOSTING_SITE_IDS = [
  'healthportal-production',
  'healthportal-pre-production',
  'healthportal-staging',
] as const

const isAllowedFirebaseHostingHostname = (hostname: string) => {
  return ALLOWED_FIREBASE_HOSTING_SITE_IDS.some((siteId) => {
    return (
      hostname === `${siteId}.web.app` ||
      hostname === `${siteId}.firebaseapp.com` ||
      ((hostname.endsWith('.web.app') || hostname.endsWith('.firebaseapp.com')) &&
        hostname.startsWith(`${siteId}--`))
    )
  })
}

const isAllowedParentOrigin = (origin: string): boolean => {
  try {
    const { hostname, protocol } = new URL(origin)
    if (
      protocol === 'https:' &&
      (hostname === 'nutrisense.io' ||
        hostname.endsWith('.nutrisense.io') ||
        isAllowedFirebaseHostingHostname(hostname))
    ) {
      return true
    }
    if (
      (protocol === 'http:' || protocol === 'https:') &&
      (hostname === 'localhost' || hostname === '127.0.0.1')
    ) {
      return true
    }
    return false
  } catch {
    return false
  }
}

const normalizeEmail = (email: string | null | undefined) => email?.trim().toLowerCase() ?? ''

const getRequestedUserEmail = (data: unknown) => {
  if (!data || typeof data !== 'object' || !('userEmail' in data)) {
    return null
  }

  const { userEmail } = data as { userEmail?: unknown }
  if (typeof userEmail !== 'string') {
    return null
  }

  const trimmedUserEmail = userEmail.trim()

  return trimmedUserEmail || null
}

const getErrorMessage = (error: unknown) => {
  if (error instanceof Error && error.message) {
    return error.message
  }

  return 'Unable to switch presentation mode.'
}

export const useIframeImpersonation = (adminLogin: Login | undefined) => {
  const adminEmail = adminLogin?.user?.email
  const showSnack = useSnack()
  const dispatchAsync = useDispatchAsync()
  const currentUser = useSelector(userSelector)
  const isPresentationFeatureEnabled = useFeatureFlag(Feature.MobileAppPresentationMode)
  const [endpoint] = useStorageValue<string>('endpoint')
  const inFlightUserEmailRef = useRef<string | null>(null)

  useEffect(() => {
    const browserWindow = getBrowserWindow()

    const postImpersonationResult = (message: IframeImpersonationResultMessage) => {
      browserWindow.parent?.postMessage?.(message, '*')
    }

    const reportFailure = (
      requestedUserEmail: string,
      reason: IframeImpersonationFailureReason,
      error?: unknown,
    ) => {
      if (error) {
        showSnack(getErrorMessage(error))
      }

      postImpersonationResult({
        type: 'member_impersonation_failed',
        reason,
        userEmail: requestedUserEmail,
      })
    }

    const reportSuccess = (requestedUserEmail: string) => {
      postImpersonationResult({
        type: 'member_impersonated',
        userEmail: requestedUserEmail,
      })
    }

    const impersonateMember = async (requestedUserEmail: string) => {
      const normalizedRequestedUserEmail = normalizeEmail(requestedUserEmail)

      if (!adminLogin || !adminEmail) {
        reportFailure(requestedUserEmail, 'missing_admin_context')
        return
      }

      if (!endpoint) {
        reportFailure(requestedUserEmail, 'missing_endpoint')
        return
      }

      if (normalizeEmail(currentUser?.email) === normalizedRequestedUserEmail) {
        reportSuccess(requestedUserEmail)
        return
      }

      if (inFlightUserEmailRef.current) {
        if (inFlightUserEmailRef.current === normalizedRequestedUserEmail) {
          return
        }

        reportFailure(requestedUserEmail, 'conflicting_request')
        return
      }

      inFlightUserEmailRef.current = normalizedRequestedUserEmail

      try {
        try {
          await dispatchAsync({
            type: 'app/logout',
          })
        } catch (error) {
          reportFailure(requestedUserEmail, 'logout_failed', error)
          return
        }

        Storage.set('login', adminLogin)
        Storage.set('admin_login', adminLogin)

        Apollo.resetEndpoint({ env: App.build.env, endpoint })

        let impersonatedLogin: Login
        try {
          impersonatedLogin = await dispatchAsync<Login>({
            type: 'app/login',
            payload: {
              email: adminEmail,
              password: '',
              impersonate: requestedUserEmail,
            },
          })
        } catch (error) {
          reportFailure(requestedUserEmail, 'login_failed', error)
          return
        }

        if (normalizeEmail(impersonatedLogin.user?.email) === normalizedRequestedUserEmail) {
          reportSuccess(requestedUserEmail)
          return
        }

        reportFailure(requestedUserEmail, 'user_mismatch')
      } finally {
        if (inFlightUserEmailRef.current === normalizedRequestedUserEmail) {
          inFlightUserEmailRef.current = null
        }
      }
    }

    const handleMemberDataMessage = (event: MessageEvent) => {
      if (!isAllowedParentOrigin(event.origin)) {
        return
      }

      if (event.data?.type !== 'member_data' || !isPresentationFeatureEnabled) {
        return
      }

      const requestedUserEmail = getRequestedUserEmail(event.data)
      if (!requestedUserEmail) {
        return
      }

      void impersonateMember(requestedUserEmail)
    }

    browserWindow.addEventListener?.('message', handleMemberDataMessage)
    return () => browserWindow.removeEventListener?.('message', handleMemberDataMessage)
  }, [
    adminEmail,
    adminLogin,
    currentUser?.email,
    dispatchAsync,
    endpoint,
    isPresentationFeatureEnabled,
    showSnack,
  ])

  const hasAdmin = !!adminEmail

  useEffect(() => {
    if (hasAdmin && isPresentationFeatureEnabled) {
      getBrowserWindow().parent?.postMessage?.({ type: 'admin_authenticated' }, '*')
    }
  }, [hasAdmin, isPresentationFeatureEnabled])
}
